import React from "react";
import { useTheme } from "../context/ThemeContext";
import ListingCard from "./ListingCard";
import "./Directory.css";

/**
 * Featured attorney listings on the consumer Ask page.
 *
 * Fed by the `featured` event of /consumer/analyze-stream: the backend runs a
 * lightweight practice-area classifier on the question, in parallel with the
 * analysis models, and matches the result plus the visitor's state against the
 * featured directory listings. The count is whatever matched (1 to 3), never a
 * hardcoded number, and when more than three qualify the backend rotates
 * fairly among them.
 *
 * `matchType` says how the listings were chosen —
 *   "practiceArea": state + the classified category, or a custom specialty
 *                   the question mentions;
 *   "state":        nothing matched the practice area, so these are simply the
 *                   featured listings in the visitor's state;
 *   "none":         no featured listings in the state at all — the Coming Soon
 *                   placeholder shows instead.
 *
 * This is a DIFFERENT product from the statewide banner ad. Featured Listings
 * are a directory placement; the banner is bought advertising. They must not be
 * merged into one system.
 *
 * @param {Array} listings       - matched featured listings
 * @param {boolean} loading      - show skeletons while the match is in flight
 * @param {number} skeletonCount - how many skeletons to show while loading (1-3)
 * @param {string} matchType     - see above
 * @param {string} category      - the classified practice area ("Other" when none)
 * @param {string} usState       - the state the match was made for
 */
export default function FeaturedAttorneys({
  listings = [],
  loading = false,
  skeletonCount = 1,
  matchType = "none",
  category = "Other",
  usState = "",
}) {
  const { darkMode } = useTheme();

  const shell = {
    marginBottom: "20px",
    padding: "22px 24px",
    borderRadius: "14px",
    background: darkMode ? "#1e293b" : "#fff",
    border: `1px solid ${darkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
  };

  const eyebrowStyle = {
    margin: "0 0 6px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: darkMode ? "#64748b" : "#94a3b8",
  };

  const titleStyle = {
    margin: "0 0 3px",
    fontSize: "15px",
    fontWeight: 700,
    color: darkMode ? "#e2e8f0" : "#1e293b",
  };

  const subStyle = {
    margin: 0,
    fontSize: "13px",
    lineHeight: 1.45,
    color: darkMode ? "#94a3b8" : "#64748b",
  };

  const count = Math.min(Math.max(skeletonCount, 1), 3);
  const hasListings = !loading && listings.length > 0;

  // ── Real listings ─────────────────────────────────────────────────────────
  if (hasListings) {
    const where = usState ? ` in ${usState}` : "";
    const byPracticeArea = matchType === "practiceArea";
    const plural = listings.length === 1 ? "" : "s";

    return (
      <section style={shell} aria-label="Featured attorneys and paralegals">
        <div style={{ marginBottom: "16px" }}>
          <p style={eyebrowStyle}>Featured Attorneys/Paralegals</p>
          <p style={titleStyle}>
            {byPracticeArea
              ? `Featured listing${plural}${where} for ${category}`
              : `Featured listing${plural}${where}`}
          </p>
          <p style={subStyle}>
            {byPracticeArea
              ? "Matched to your state and the type of legal issue you described."
              : "Matched to your state. None currently list the practice area your question falls under."}
          </p>
        </div>

        <div className={`fa-listings${darkMode ? " fa-listings-dark" : ""}`}>
          {listings.map((item) => (
            <ListingCard listing={item} key={item._id} />
          ))}
        </div>
      </section>
    );
  }

  // ── Loading, or nothing to show ───────────────────────────────────────────
  return (
    <section
      style={{
        ...shell,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "24px",
        flexWrap: "wrap",
      }}
      aria-label="Featured attorneys and paralegals"
      aria-busy={loading}
    >
      <div style={{ minWidth: "220px" }}>
        <p style={eyebrowStyle}>
          {loading ? "Finding featured attorneys" : "Coming Soon"}
        </p>
        <p style={titleStyle}>
          {loading
            ? "Matching featured attorneys to your question"
            : "Featured attorney listings will appear here"}
        </p>
        <p style={subStyle}>
          {loading
            ? "Looking for featured attorneys in your state who handle this kind of issue."
            : "In the future, we'll show featured attorneys matched to your state and legal issue."}
        </p>
      </div>

      <div className="fa-cards">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="fa-skeleton"
            style={{
              background: darkMode ? "#0f172a" : "#f8fafc",
              border: `1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "#edf1f7"}`,
            }}
          >
            <div
              className="fa-skeleton-avatar"
              style={{ background: darkMode ? "#334155" : "#e2e8f0" }}
            />
            <div className="fa-skeleton-lines">
              <div style={{ background: darkMode ? "#334155" : "#e2e8f0" }} />
              <div
                style={{
                  background: darkMode ? "#334155" : "#e2e8f0",
                  width: "60%",
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
